import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      navigate('/pricing');
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data: d } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setData(d);
        if (d.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (d.status === 'expired') {
          setStatus('expired');
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, navigate]);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-24 min-h-[70vh]">
      <div className="max-w-3xl">
        {status === 'checking' && (
          <div data-testid="checkout-checking">
            <Loader2 size={32} className="text-[#D4AF37] animate-spin mb-8" strokeWidth={1} />
            <h1 className="text-4xl md:text-5xl font-extralight leading-tight tracking-tight mb-6">
              Settling under<br />
              <span className="font-display italic text-[#D4AF37]">sovereign seal…</span>
            </h1>
            <p className="text-base text-[#A3A3A3] font-light">Verifying your transaction with the Stripe corridor.</p>
          </div>
        )}
        {status === 'paid' && data && (
          <div data-testid="checkout-success">
            <CheckCircle2 size={40} className="text-[#D4AF37] mb-8" strokeWidth={1} />
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">{data.product_name}</div>
            <h1 className="text-4xl md:text-5xl font-extralight leading-tight tracking-tight mb-8">
              Settlement<br />
              <span className="font-display italic text-[#D4AF37]">cleared.</span>
            </h1>
            <p className="text-base text-[#A3A3A3] font-light leading-relaxed mb-10 max-w-2xl">
              ${data.amount_usd?.toFixed(2) || '0.00'} has crossed the corridor. Your dossier
              will be transmitted under admiral seal within 24 hours.
            </p>
            {data.lineage_split && (
              <div className="sov-border bg-[#0A0A0A] p-6 mb-10 max-w-xl">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Lineage Law · 75 / 15 / 10</div>
                <div className="space-y-3 text-sm">
                  <Row l="Admiral · Mercy" v={data.lineage_split.admiral_usd} />
                  <Row l="Operations · Vessel" v={data.lineage_split.operations_usd} />
                  <Row l="CyberKids Heritage Fund" v={data.lineage_split.cyberkids_usd} />
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-6">
              <Link to="/dashboard" className="btn-sovereign">Command Deck <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
              <Link to="/vault" className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] self-center">Return to Vault →</Link>
            </div>
          </div>
        )}
        {(status === 'expired' || status === 'timeout' || status === 'error') && (
          <div>
            <h1 className="text-4xl font-extralight mb-6">The corridor is recalibrating.</h1>
            <p className="text-[#A3A3A3] mb-8">Status: {status}. Your card will not be double-charged. Try again from Pricing.</p>
            <Link to="/pricing" className="btn-sovereign">Return to Pricing</Link>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ l, v }) => (
  <div className="flex justify-between border-b border-[#D4AF37]/15 pb-2">
    <span className="text-[#A3A3A3] tracking-wide">{l}</span>
    <span className="font-display text-[#F5F5F5]">${v?.toFixed(2) || '0.00'}</span>
  </div>
);
